import React, { useCallback, useEffect, useRef } from "react";
import { inject, observer } from "mobx-react";
import BroadcastStreamStore from "../_stores/broadcast-stream/BroadcastStreamStore";
import { BroadcastStreamType } from "../_stores/broadcast-stream/enums";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindSpinner from "library/components/_tailwind/spinner";

interface MobileBroadcastPreviewProps {
  broadcastStreamStore?: BroadcastStreamStore;
}

const MobileBroadcastPreview: React.FC<MobileBroadcastPreviewProps> = ({
  broadcastStreamStore,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { mediaStream, streamType, isGatheringMediaDeviceData, setStreamType } =
    broadcastStreamStore!;

  // Auto-select WebRTC on mobile (OBS not available on mobile devices)
  useEffect(() => {
    if (!streamType) {
      setStreamType(BroadcastStreamType.WEBRTC);
    }
  }, [streamType, setStreamType]);

  useEffect(() => {
    if (videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream;
    }
  }, [mediaStream]);

  // iOS Safari: Resume video playback when tab regains focus
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "visible" && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay may be blocked, ignore error
      });
    }
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  const isLoading = isGatheringMediaDeviceData || !mediaStream;
  const showVideo = mediaStream && streamType === BroadcastStreamType.WEBRTC;

  return (
    <TailwindFlex
      position='absolute'
      inset={["inset-0"]}
      alignItems='items-center'
      justifyContent='justify-center'
      backgroundColor='bg-black'>
      {isLoading && (
        <TailwindFlex
          flexDirection='flex-col'
          alignItems='items-center'
          justifyContent='justify-center'
          gap='gap-4'>
          <TailwindSpinner />
        </TailwindFlex>
      )}

      {showVideo && (
        <video
          ref={videoRef}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: "scaleX(-1) translateZ(0)",
            WebkitTransform: "scaleX(-1) translateZ(0)",
          }}
          autoPlay
          muted
          playsInline
          disablePictureInPicture
          controls={false}
        />
      )}
    </TailwindFlex>
  );
};

export default inject("broadcastStreamStore")(observer(MobileBroadcastPreview));
